import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { getUserConnections, addUserConnection, UserConnection } from '../../../lib/database';
import NangoAuth from '../../../components/ui/NangoAuth';
import { ChevronUpIcon, ChevronDownIcon } from '../../../components/ui/simple-icons';

interface ConnectionManagerDrawerProps {
  agentId: string;
  requiredProviders: { name: string; provider: string }[];
  onActiveConnectionsChange: (connections: Record<string, string>) => void;
}

const ConnectionManagerDrawer: React.FC<ConnectionManagerDrawerProps> = ({ agentId, requiredProviders, onActiveConnectionsChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [connections, setConnections] = useState<UserConnection[] | null>(null);
  const [activeConnections, setActiveConnections] = useState<Record<string, string>>({});
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserAndConnections = async () => {
      // Reset to null on prop change to indicate a new loading cycle
      setConnections(null);

      if (requiredProviders.length === 0) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setUserId(user.id);
        const userConnections = await getUserConnections(user.id);
        
        const initialActive: Record<string, string> = {};
        const allAreConnected = requiredProviders.every(({ provider }) => {
          const firstConn = userConnections.find(c => c.oauth_provider === provider);
          if (firstConn) {
            initialActive[provider] = firstConn.connection_id;
            return true;
          }
          return false;
        });
        
        setActiveConnections(initialActive);
        onActiveConnectionsChange(initialActive);
        setIsOpen(!allAreConnected);
        setConnections(userConnections); // Set connections last to trigger render
      } else {
        setIsOpen(true);
        setConnections([]); // No user, but we are done loading
      }
    };
    fetchUserAndConnections();
  }, [agentId, requiredProviders, onActiveConnectionsChange]);

  const handleConnectionSuccess = async (providerConfigKey: string, connectionId: string) => {
    if (!userId || connections === null) {
      console.error("Cannot save connection, user is not logged in or connections not loaded.");
      return;
    }
    try {
      const newConnection = await addUserConnection(userId, providerConfigKey, connectionId);
      const updatedConnections = [...connections, newConnection];
      setConnections(updatedConnections);
      handleConnectionSelect(providerConfigKey, newConnection.connection_id);
    } catch (error) {
      console.error('Error saving connection directly to Supabase:', error);
    }
  };
  
  const handleConnectionSelect = (provider: string, connectionId: string) => {
    const newActive = { ...activeConnections, [provider]: connectionId };
    setActiveConnections(newActive);
    onActiveConnectionsChange(newActive);
  };

  const handleDisconnect = async (provider: string) => {
    if (connections === null) return;
    const updatedConnections = connections.filter(c => c.oauth_provider !== provider);
    setConnections(updatedConnections);
    
    const newActive = { ...activeConnections };
    delete newActive[provider];
    setActiveConnections(newActive);
    onActiveConnectionsChange(newActive);
  };

  const renderProviderControl = (name: string, provider: string) => {
    const isConnected = connections?.some(c => c.oauth_provider === provider);

    if (isConnected) {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-xs text-green-600 font-medium">Connected</span>
          <button
            onClick={() => handleDisconnect(provider)}
            className="text-xs px-2 py-1 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            Disconnect
          </button>
        </div>
      );
    }

    return (
      <NangoAuth
        providerConfigKey={provider}
        connectionId={`user_${userId}_${provider}_${Date.now()}`}
        onConnectionSuccess={(result) => handleConnectionSuccess(provider, result.connectionId)}
        onConnectionError={(err) => console.error(`Nango auth error for ${provider}:`, err)}
      />
    );
  };

  // Do not render until connections have been loaded (i.e., not null)
  if (connections === null || requiredProviders.length === 0) {
    return null;
  }

  const allConnected = requiredProviders.every(({ provider }) => 
    connections.some(c => c.oauth_provider === provider)
  );
  
  const canCollapse = allConnected;

  return (
    <div className="border-b border-gray-200">
      <div
        onClick={canCollapse ? () => setIsOpen(!isOpen) : undefined}
        className={`px-4 py-3 flex justify-between items-center ${canCollapse ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}
      >
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          {!allConnected && <div className="w-2 h-2 rounded-full bg-red-500" />}
          <span className="text-sm font-medium text-gray-700">Connections</span>
        </div>
        {canCollapse && (isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />)}
      </div>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen || !allConnected ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="px-4 pb-3 pt-3 space-y-3">
          {requiredProviders.map(({ name, provider }) => {
            const providerName = provider.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const isConnected = connections.some(c => c.oauth_provider === provider);
            
            return (
              <div key={provider} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {!isConnected && <div className="w-2 h-2 rounded-full bg-red-500" />}
                  {isConnected && <div className="w-2 h-2" />}
                  <span className="text-sm font-medium text-gray-700">{providerName}</span>
                </div>
                <div>
                  {renderProviderControl(providerName, provider)}
                </div>
              </div>
            );
  
          })}
        </div>
      </div>
    </div>
  );
};

export default ConnectionManagerDrawer;
